import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  and,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentData,
  documentId,
  Firestore,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  Query,
  QuerySnapshot,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { NGXLogger } from 'ngx-logger';
import { intersects } from 'radash';
import { catchError, from, map, Observable, of, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppException, AppExceptionModel } from '../models/app-exception.model';
import { ProtestantCanonBooks } from '../models/books.bible.model';
import { SchoolMemberType } from '../models/member.school.model';
import { MemberType } from '../models/membership.status';
import { QuizQuestion } from '../models/question.quiz.model';
import { Quiz, QuizStatus } from '../models/quiz.model';

@Injectable({
  providedIn: 'root',
})
export class QuizService {
  private afs = inject(Firestore);
  private logger = inject(NGXLogger);

  constructor() {}

  getQuizzes({
    groupId,
    memberType,
    userId, // Ensure you pass the current user's ID here
    lastRecord,
    limitRecords,
  }: {
    groupId: string;
    memberType: string[]; // Pass the memberType for role-based filtering
    userId: string;
    lastRecord?: any;
    limitRecords?: number;
  }): Observable<Quiz[]> {
    const quizRef = collection(
      this.afs,
      environment.FIRESTORE_GROUPS_COLLECTION,
      groupId,
      environment.FIRESTORE_QUIZZES_COLLECTION,
    );
    let q: Query<DocumentData> = query(quizRef);

    // Role-based filtering
    const isAdmin = intersects(memberType, [
      SchoolMemberType.president,
      SchoolMemberType.principal,
      SchoolMemberType.teacher,
      MemberType.admin,
    ]);

    if (isAdmin) {
      q = query(
        q,
        or(
          where('quizStatus', 'in', [
            QuizStatus.waiting,
            QuizStatus.inProgress,
            QuizStatus.scheduled,
            QuizStatus.completed,
            QuizStatus.cancelled,
          ]),
          and(
            where('quizStatus', '==', QuizStatus.draft),
            where('createdById', '==', userId),
          ),
        ),
      );
    } else {
      if (
        memberType.includes(SchoolMemberType.student) ||
        memberType.includes(SchoolMemberType.parent) ||
        memberType.includes(MemberType.member)
      ) {
        q = query(
          q,
          where('quizStatus', 'in', [
            QuizStatus.scheduled,
            QuizStatus.waiting,
            QuizStatus.inProgress,
            QuizStatus.completed,
          ]),
        );
      }
    }

    q = query(
      q,
      orderBy('scheduledAt', 'desc'),
      limit(limitRecords ?? 10),
      ...(lastRecord ? [startAfter(lastRecord)] : []),
    );

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map((doc) => ({ ...(doc.data() as Quiz), id: doc.id })),
      ),
    );
  }

  getQuizAttempts({
    groupId,
    quizId,
    userId,
    memberType,
    lastRecord,
    limitRecords = 10,
  }: {
    groupId: string;
    quizId: string;
    userId: string;
    memberType: string[];
    lastRecord?: any;
    limitRecords?: number;
  }): Observable<Quiz[]> {
    this.logger.debug(
      `QuizService :: getQuizAttempts :: groupId=${groupId}, quizId=${quizId}, userId=${userId}, memberType=${JSON.stringify(memberType)}, lastRecord=${JSON.stringify(lastRecord)}, limitRecords=${limitRecords}`,
    );
    let quizRef: CollectionReference;
    let q: Query<DocumentData>;

    try {
      quizRef = collection(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_QUIZZES_COLLECTION,
        quizId,
        environment.FIRESTORE_QUIZ_ATTEMPTS_COLLECTION!,
      );

      q = query(quizRef);

      q = query(
        q,
        where('userId', '==', userId),
        orderBy('userId'),
        orderBy('modified', 'desc'),
        limit(limitRecords),
      );
    } catch (error: any) {
      this.logger.error('Error configuring Quiz query (sync):', error);
      return throwError(
        () =>
          ({
            code: 'ERR_QUIZ_ATTEMPTS',
            title: 'Error Fetching Quiz Attempts',
            message: error.message || 'Error configuring query',
          }) as AppExceptionModel,
      );
    }

    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map((doc) => ({ ...(doc.data() as Quiz), id: doc.id })),
      ),
      catchError((error: any) => {
        this.logger.error(
          `Error fetching Quiz attempts (async): ${JSON.stringify(error, null, 2)}`,
        );

        return throwError(
          () =>
            ({
              code: 'ERR_QUIZ_ATTEMPTS',
              title: 'Error Fetching Quiz Attempts',
              message: error.message || 'Unknown error',
              originalError: error, // ← helpful for debugging
            }) as AppExceptionModel,
        );
      }),
    );
  }

  getQuizById({
    groupId,
    quizId,
  }: {
    groupId: string;
    quizId: string;
  }): Observable<Quiz | null> {
    this.logger.debug(
      `QuizService:: getQuizById: groupId=${groupId}, quizId=${quizId}`,
    );
    try {
      const quizRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_QUIZZES_COLLECTION,
        quizId,
      );
      return from(getDoc(quizRef)).pipe(
        map((doc) => {
          if (doc.exists()) {
            const quiz = doc.data() as Quiz;
            quiz.id = doc.id;
            return quiz;
          } else {
            return null;
          }
        }),
      );
    } catch (error: any) {
      this.logger.error(`Error fetching quiz by ID: ${JSON.stringify(error)}`);
      throw {
        code: 'ERR_FETCH_QUIZ_BY_ID',
        title: 'Error Fetching Quiz',
        message: error.error?.message || 'Error fetching quiz by ID',
      } as AppExceptionModel;
    }
  }

  getQuizAttemptById({
    groupId,
    quizId,
    attemptId,
  }: {
    groupId: string;
    quizId: string;
    attemptId: string;
  }): Observable<Quiz | null> {
    this.logger.debug(
      `QuizService:: getQuizAttemptById: groupId=${groupId}, attemptId=${attemptId}`,
    );
    try {
      const quizRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_QUIZZES_COLLECTION,
        quizId,
        environment.FIRESTORE_QUIZ_ATTEMPTS_COLLECTION!,
        attemptId,
      );
      return from(getDoc(quizRef)).pipe(
        map((doc) => {
          if (doc.exists()) {
            const quiz = doc.data() as Quiz;
            quiz.id = doc.id;
            return quiz;
          } else {
            return null;
          }
        }),
      );
    } catch (error: any) {
      this.logger.error(
        `Error fetching quiz attempt by ID: ${JSON.stringify(error)}`,
      );
      throw {
        code: 'ERR_FETCH_QUIZ_ATTEMPT_BY_ID',
        title: 'Error Fetching Quiz',
        message: error.error?.message || 'Error fetching quiz attempt by ID',
      } as AppExceptionModel;
    }
  }

  async createQuestions(
    groupId: string,
    questions: QuizQuestion[],
  ): Promise<void> {
    const qBankRef = collection(
      this.afs,
      environment.FIRESTORE_GROUPS_COLLECTION,
      groupId,
      environment.FIRESTORE_Q_BANK_COLLECTION,
    );

    // Map each question to a separate push to Firestore
    const promises = questions.map(async (question) => {
      try {
        await addDoc(qBankRef, { ...question, modified: serverTimestamp() });
      } catch (err) {
        this.logger.error(
          `Failed to upload question: ${question.questionText}`,
          err,
        );
      }
    });
    await Promise.all(promises);
  }

  async updateQuestion({
    groupId,
    questionId,
    question,
  }: {
    groupId: string;
    questionId: string;
    question: QuizQuestion;
  }): Promise<void> {
    this.logger.debug(
      `QuizService :: updateQuestion :: GroupId: ${groupId}, QuestionId: ${questionId}, Question: ${JSON.stringify(question, null, 2)}`,
    );
    const qBankRef = doc(
      this.afs,
      environment.FIRESTORE_GROUPS_COLLECTION,
      groupId,
      environment.FIRESTORE_Q_BANK_COLLECTION,
      questionId,
    );

    try {
      await updateDoc(qBankRef, { ...question, modified: serverTimestamp() });
    } catch (err) {
      this.logger.error(
        `Failed to update question: ${question.questionText}`,
        err,
      );
    }
  }

  async createQuiz(quiz: Quiz): Promise<string> {
    this.logger.debug(
      `QuizService :: createQuiz :: ${JSON.stringify(quiz, null, 2)}`,
    );
    try {
      const quizRef = collection(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        quiz.groupId,
        environment.FIRESTORE_QUIZZES_COLLECTION,
      );
      const data = await addDoc(quizRef, {
        ...quiz,
        created: serverTimestamp(),
        modified: serverTimestamp(),
      });
      return data.id;
    } catch (err) {
      this.logger.error(
        `QuizService :: Failed to create quiz: ${quiz.title}`,
        err,
      );
      throw new AppException({
        code: 'QUIZ_CREATION_FAILED',
        message: 'Failed to create quiz. Please try again later.',
      });
    }
  }

  async updateQuiz(quizId: string, quiz: Quiz) {
    this.logger.debug(
      `QuizService :: updateQuiz :: ${JSON.stringify(quiz, null, 2)}`,
    );
    try {
      const quizRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        quiz.groupId,
        environment.FIRESTORE_QUIZZES_COLLECTION,
        quizId,
      );
      const { id, created, ...updateData } = quiz;
      await updateDoc(quizRef, {
        ...updateData,
        modified: serverTimestamp(),
      });
      return;
    } catch (err) {
      this.logger.error(
        `QuizService :: Failed to update quiz: ${quiz.title}`,
        err,
      );
      throw new AppException({
        code: 'QUIZ_UPDATE_FAILED',
        message: 'Failed to update quiz. Please try again later.',
      });
    }
  }

  async deleteQuiz(groupId: string, quizId: string): Promise<void> {
    this.logger.debug(
      `QuizService :: deleteQuiz :: GroupId: ${groupId}, QuizId: ${quizId}`,
    );
    try {
      const quizRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_QUIZZES_COLLECTION,
        quizId,
      );
      await deleteDoc(quizRef);
    } catch (err) {
      this.logger.error(`QuizService :: Failed to delete quiz: ${quizId}`, err);
      throw new AppException({
        code: 'QUIZ_DELETE_FAILED',
        message: 'Failed to delete quiz. Please try again later.',
      });
    }
  }

  getQuestions(groupId: string, filter: any): Observable<QuizQuestion[]> {
    this.logger.debug(
      `QuizService :: Searching questions in group: ${groupId} with filters: ${JSON.stringify(filter)}`,
    );

    let reference = filter;
    let text = '';
    let tags: string[] = [];

    // Check if unified search params object is passed
    if (
      filter &&
      (filter.reference !== undefined ||
        filter.text !== undefined ||
        filter.tags !== undefined)
    ) {
      reference = filter.reference;
      text = filter.text || '';
      tags = filter.tags || [];
    }

    const hasText = !!(text && text.trim());
    const hasTags = !!(tags && tags.length > 0);

    if (hasText || hasTags) {
      const functions = getFunctions();
      const searchFn = httpsCallable(functions, 'quiz_searchQuestions', {
        limitedUseAppCheckTokens: true,
      });
      return from(
        searchFn({
          groupId,
          text,
          tags,
          reference,
        }),
      ).pipe(map((res: any) => res.data as QuizQuestion[]));
    }

    try {
      if (!reference || !reference.book) {
        return of([]);
      }
      // Build the collection reference with conditional logic
      const bankRef = collection(
        this.afs,
        `${environment.FIRESTORE_GROUPS_COLLECTION}/${groupId}/${environment.FIRESTORE_Q_BANK_COLLECTION}`,
      );

      // Mandatory filters
      let q: Query<DocumentData> = query(bankRef);

      const searchBook = reference.book.trim().toLowerCase();
      const enumValue = Object.values(ProtestantCanonBooks).find(
        (b) => b.toLowerCase() === searchBook,
      );

      const searchTerms = [searchBook, reference.book];
      if (enumValue) {
        searchTerms.push(enumValue);
        searchTerms.push(enumValue.toLowerCase());
      }
      const uniqueTerms = Array.from(new Set(searchTerms));

      q = query(
        q,
        or(...uniqueTerms.map((term) => where('reference.book', '==', term))),
      );

      if (
        reference.chapter !== undefined &&
        reference.chapter !== null &&
        reference.chapter !== ''
      ) {
        q = query(
          q,
          where('reference.chapter', '==', Number(reference.chapter)),
        );
      }

      // Conditional filter for a range (from/to)
      if (reference.fromVerse) {
        q = query(
          q,
          where('reference.verse', '>=', Number(reference.fromVerse)),
        );
      }
      if (reference.toVerse) {
        q = query(q, where('reference.verse', '<=', Number(reference.toVerse)));
      }

      q = query(q, orderBy('reference.verse', 'asc'));

      return from(getDocs(q)).pipe(
        map((snapshot: QuerySnapshot<DocumentData>) => {
          return snapshot.docs.map((doc) => {
            const question = doc.data() as QuizQuestion;
            question.id = doc.id;
            return question;
          });
        }),
      );
    } catch (error: any) {
      this.logger.error(
        `Error fetching questions in group: ${groupId} with reference: ${JSON.stringify(reference)} - ${JSON.stringify(error)}`,
      );
      return of([]);
    }
  }

  getQuestionById(
    groupId: string,
    questionId: string,
  ): Observable<QuizQuestion | undefined> {
    this.logger.debug(
      `QuizService :: Get question by ID: ${questionId} in group: ${groupId}`,
    );

    try {
      // Build the collection reference with conditional logic
      const questionRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_Q_BANK_COLLECTION,
        questionId,
      );

      return from(getDoc(questionRef)).pipe(
        map((doc) => {
          if (doc.exists()) {
            const question = doc.data() as QuizQuestion;
            question.id = doc.id;
            return question;
          } else {
            return undefined;
          }
        }),
      );
    } catch (error: any) {
      this.logger.error(
        `Error fetching question by ID: ${JSON.stringify(error)}`,
      );
      throw {
        code: 'ERR_FETCH_QUESTION_BY_ID',
        title: 'Error Fetching Question',
        message: error.error?.message || 'Error fetching question by ID',
      } as AppExceptionModel;
    }
  }

  async getQuizQuestions(
    groupId: string,
    questionIds: string[],
  ): Promise<QuizQuestion[]> {
    this.logger.debug(
      `QuizService :: Fetching quiz questions for GroupId: ${groupId} with QuestionIds: ${JSON.stringify(questionIds)}`,
    );
    try {
      const q = query(
        collection(
          this.afs,
          environment.FIRESTORE_GROUPS_COLLECTION,
          groupId,
          environment.FIRESTORE_Q_BANK_COLLECTION,
        ),
        where(documentId(), 'in', questionIds),
      );

      const querySnapshot = await getDocs(q);
      this.logger.debug(
        `QuizService :: Fetched ${querySnapshot.docs.length} quiz questions for GroupId: ${groupId}`,
      );
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as QuizQuestion[];
    } catch (error: any) {
      this.logger.error(
        `QuizService :: Error fetching quiz questions for GroupId: ${groupId} with QuestionIds: ${JSON.stringify(questionIds)}`,
        error,
      );
      throw {
        code: 'ERR_FETCH_QUIZ_QUESTIONS',
        title: 'Error Fetching Quiz Questions',
        message: error.message || 'Error fetching quiz questions',
      } as AppExceptionModel;
    }
  }

  /**
   * Get user's answers for a scheduled quiz attempt.
   *
   * @param groupId
   * @param quizId
   * @param attemptId
   * @param userId - The ID of the user whose answers we want to fetch. This should be the currently authenticated user's ID.
   * @returns
   */
  async getUserAnswers(
    groupId: string,
    quizId: string,
    attemptId: string,
    userId: string,
  ): Promise<QuizQuestion[]> {
    this.logger.debug(
      `QuizService :: Fetching quiz questions for GroupId: ${groupId} with QuizId: ${quizId} and AttemptId: ${attemptId}`,
    );
    if (!groupId || !quizId || !attemptId || !userId) {
      this.logger.error(
        `QuizService :: Invalid parameters for fetching user answers. GroupId: ${groupId}, QuizId: ${quizId}, AttemptId: ${attemptId}, UserId: ${userId}`,
      );
      throw {
        code: 'ERR_INVALID_PARAMETERS',
        title: 'Invalid Parameters',
        message:
          'Group ID, Quiz ID, Attempt ID, and User ID are required to fetch user answers.',
      } as AppExceptionModel;
    }
    try {
      const q = query(
        collection(
          this.afs,
          environment.FIRESTORE_GROUPS_COLLECTION,
          groupId,
          environment.FIRESTORE_QUIZZES_COLLECTION,
          quizId,
          environment.FIRESTORE_QUIZ_ATTEMPTS_COLLECTION,
          attemptId,
          environment.FIRESTORE_USER_ANSWERS_COLLECTION,
        ),
        where('attemptId', '==', attemptId),
        where('userId', '==', userId),
        limit(100), // Limit to 100 answers to prevent excessive data fetching. Adjust as needed.
      );

      const querySnapshot = await getDocs(q);
      this.logger.debug(`QuizService :: Fetched ${querySnapshot.docs.length}`);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as QuizQuestion[];
    } catch (error: any) {
      this.logger.error(
        `QuizService :: Error fetching user answers for GroupId: ${groupId} with QuizId: ${quizId} and AttemptId: ${attemptId} for UserId: ${userId}`,
        error,
      );
      throw {
        code: 'ERR_FETCH_USER_ANSWERS',
        title: 'Error Fetching User Answers',
        message: error.message || 'Error fetching user answers',
      } as AppExceptionModel;
    }
  }

  // Edit Question
  async editQuestion(
    groupId: string,
    questionId: string,
    updatedQuestion: QuizQuestion,
  ): Promise<void> {
    try {
      const questionRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_Q_BANK_COLLECTION,
        questionId,
      );
      await updateDoc(questionRef, {
        ...updatedQuestion,
        modified: serverTimestamp(),
      });
    } catch (err) {
      this.logger.error(
        `Failed to update question with ID: ${questionId}`,
        err,
      );
      throw new AppException({
        code: 'QUESTION_UPDATE_FAILED',
        message: 'Failed to update question. Please try again later.',
      });
    }
  }

  // Delete Question
  async deleteQuestion(groupId: string, questionId: string): Promise<void> {
    try {
      const questionRef = doc(
        this.afs,
        environment.FIRESTORE_GROUPS_COLLECTION,
        groupId,
        environment.FIRESTORE_Q_BANK_COLLECTION,
        questionId,
      );
      await deleteDoc(questionRef);
    } catch (err) {
      this.logger.error(
        `Failed to delete question with ID: ${questionId}`,
        err,
      );
      throw new AppException({
        code: 'QUESTION_DELETE_FAILED',
        message: 'Failed to delete question. Please try again later.',
      });
    }
  }

  /**
   * Once the questions are generated on client side by user (with/without using vertexAI),
   * send the questions to firebase function for storing in firestore.
   *
   * The callable function creates AI embeddings for the quiz question.
   *
   * @param groupId
   * @param questions
   * @param chatUsage
   * @returns
   */
  async createQuestionsWithBilling(
    groupId: string,
    questions: QuizQuestion[],
    chatUsage?: {
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
    },
  ): Promise<{ success: boolean; questions: QuizQuestion[]; tokensUsed: any }> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'quiz_createQuestions', {
        limitedUseAppCheckTokens: true,
      });
      const result = await callable({
        groupId,
        questions,
        chatUsage,
      });
      this.logger.debug(
        `QuizService :: createQuestionsWithBilling : Successfully created quiz questions with billing. Result: ${JSON.stringify(result, null, 2)}`,
      );
      return result.data as any;
    } catch (err) {
      this.logger.error(
        `QuizService :: createQuestionsWithBilling : Failed to create quiz questions with billing for group: ${groupId}`,
        err,
      );
      throw new AppException({
        code: 'QUIZ_CREATE_QUESTIONS_FAILED',
        message:
          'Failed to save generated quiz questions. Please check your AI token balance and try again.',
      });
    }
  }

  async generateAttemptDocument(
    groupId: string,
    quizId: string,
    userProfile?: { name?: string; photoURL?: string },
  ): Promise<string> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'quiz_createQuizAttempt', {
        limitedUseAppCheckTokens: true,
      });
      const result = await callable({
        groupId,
        quizId,
        userName: userProfile?.name || '',
        userPhotoUrl: userProfile?.photoURL || '',
      });
      this.logger.debug(
        `QuizService :: generateAttemptDocument : Successfully created quiz attempt document. Result: ${JSON.stringify(result, null, 2)}`,
      );
      return result.data as string;
    } catch (err) {
      this.logger.error(
        `QuizService :: generateAttemptDocument : Failed to create quiz attempt document for QuizId: ${quizId}`,
        err,
      );
      throw new AppException({
        code: 'QUIZ_ATTEMPT_CREATION_FAILED',
        message: 'Failed to create quiz attempt. Please try again later.',
      });
    }
  }

  getQuizAnalytics(groupId: string, quizId: string): Observable<any | null> {
    const analyticsRef = doc(
      this.afs,
      environment.FIRESTORE_GROUPS_COLLECTION,
      groupId,
      environment.FIRESTORE_QUIZZES_COLLECTION,
      quizId,
      'analytics',
      'summary',
    );
    return from(getDoc(analyticsRef)).pipe(
      map((docSnap) => {
        if (docSnap.exists()) {
          return docSnap.data();
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(`Error fetching Quiz analytics:`, error);
        return of(null);
      }),
    );
  }

  getAllQuizAttempts({
    groupId,
    quizId,
    limitRecords = 50,
  }: {
    groupId: string;
    quizId: string;
    limitRecords?: number;
  }): Observable<any[]> {
    const attemptsRef = collection(
      this.afs,
      environment.FIRESTORE_GROUPS_COLLECTION,
      groupId,
      environment.FIRESTORE_QUIZZES_COLLECTION,
      quizId,
      environment.FIRESTORE_QUIZ_ATTEMPTS_COLLECTION!,
    );
    const q = query(
      attemptsRef,
      orderBy('modified', 'desc'),
      limit(limitRecords),
    );
    return from(getDocs(q)).pipe(
      map((snapshot) =>
        snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
      ),
      catchError((error) => {
        this.logger.error(`Error fetching all quiz attempts:`, error);
        return of([]);
      }),
    );
  }

  async runReportManually(groupId: string, quizId: string): Promise<boolean> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'quiz_runReportManually', {
        limitedUseAppCheckTokens: true,
      });
      const result = await callable({
        groupId,
        quizId,
      });
      this.logger.debug(
        `QuizService :: runReportManually : Successfully triggered report generation. Result: ${JSON.stringify(result, null, 2)}`,
      );
      const data = result.data as any;
      return data?.success === true;
    } catch (err) {
      this.logger.error(
        `QuizService :: runReportManually : Failed to trigger report generation for QuizId: ${quizId}`,
        err,
      );
      throw err;
    }
  }
}
